import type { Location } from '@/types/Location'
import { useHttp } from "@/composables/useHttp"
import type { SearchedEstablishment } from "@/types/SearchedEstablishment"
import { Distribution } from '@/enums/Distribution'
import type { MarketplaceSortingKey } from "@/enums/MarketplaceSortingKey"

type Paginated<T> = {
  data: T[]
  links: {
    first: string
    last: string
    prev: string | null
    next: string | null
  }
  meta: {
    current_page: number
    from: number
    last_page: number
    links: {
      active: boolean
      label: string
      url: string | null
    }[]
    path: string
    per_page: number
    to: number
    total: number
  }
}

export type EstablishmentsFilter = {
  distribution: Distribution
  lat?: number
  lng?: number
  q: string | null
  categories?: string[]
  sort: MarketplaceSortingKey
  per_page?: number
  page?: number
}

export type EstablishmentsSearchFilter = {
  distribution: Distribution
  location: {
    lat: number
    lng: number
  }
  q: string | null
  sort: MarketplaceSortingKey
}

export function useEstablishmentHttp() {
  const http = useHttp()

  async function establishmentsByCityAndCountry(city: string, country: string, categories: string[]) {
    const response = await http.get(`countries/${country}/cities/${city}/establishments/seo`, {
      params: {
        categories,
      },
    })

    return response.data.data
  }

  async function establishments(filters: EstablishmentsFilter): Promise<Paginated<object>> {
    return (await http.get('establishments', {
      params: filters,
    })).data
  }

  async function searchEstablishments(filters: EstablishmentsSearchFilter): Promise<{
    count: number,
    establishments: Array<SearchedEstablishment>
  }> {
    const response = await http.get('establishments/search', {
      params: filters,
    })

    return response.data.data
  }

  async function establishment(slug: string, country: string, city: string, hash: string | null = null) {
    const params = {}

    if (hash !== null) {
      params.hash = hash
    }

    return (await http.get(`countries/${country}/cities/${city}/establishments/${slug}`, {
      params: params,
    })).data.data
  }

  async function establishmentByDistributionType(slug: string, country: string, city: string, distribution: Distribution, location: Location | null = null) {
    const params = {}

    // only delivery requires a location
    if (location !== null) {
      params.lat = location.coordinates.lat
      params.lng = location.coordinates.lng
    }

    return (await http.get(`/countries/${country}/cities/${city}/establishments/${slug}/distributions/${distribution}`, {
      params: params,
    })).data.data
  }

  async function fakePaymentMethods(slug: string, country: string, city: string) {
    const { data } = await http.get(`/countries/${country}/cities/${city}/establishments/${slug}/payments/integrations/fake/methods`)

    return data.data
  }

  async function molliePaymentMethods(establishment: string, country: string, city: string, amount: number) {
    const { data } = await http.get(`/countries/${country}/cities/${city}/establishments/${establishment}/payments/integrations/mollie/methods?amount=${amount}`)

    return data.data
  }

  async function discounts(establishment: string, country: string, city: string): Promise<Discount[]> {
    const response = await http.get(`/countries/${country}/cities/${city}/establishments/${establishment}/discounts`)

    return response.data.data
  }

  async function establishmentsByCategory(category: string) {
    const { data } = await http.get(`/categories/${category}/establishments`)

    return data.data
  }

  async function faq(establishment: string, country: string, city: string) {
    const { data } = await http.get(`/countries/${country}/cities/${city}/establishments/${establishment}/faqs`)

    return data.data
  }

  async function reviews(establishment: string, country: string, city: string) {
    const response = await http.get(`/countries/${country}/cities/${city}/establishments/${establishment}/reviews`)

    return response.data.data
  }

  async function timeslots(establishment: string, country: string, city: string, params: any) {
    const { data } = await http.post(`/countries/${country}/cities/${city}/establishments/${establishment}/timeslots`, params)

    return data.data
  }

  return {
    fakePaymentMethods,
    molliePaymentMethods,
    establishment,
    establishmentByDistributionType,
    discounts,
    faq,
    reviews,
    establishments,
    establishmentsByCityAndCountry,
    establishmentsByCategory,
    timeslots,
    searchEstablishments,
  }
}
